import { formatEntityTerm, SupplierTerminologyMode } from '@scrambl/shared'

export type SupplierTerm = {
  singular: string
  plural: string
  Singular: string
  Plural: string
  mode: SupplierTerminologyMode
  isVendor: boolean
  isLoading: boolean
  organizationId?: string
}

const TERMS: Record<
  SupplierTerminologyMode,
  Pick<SupplierTerm, 'singular' | 'plural'>
> = {
  [SupplierTerminologyMode.SUPPLIER]: {
    singular: 'supplier',
    plural: 'suppliers',
  },
  [SupplierTerminologyMode.VENDOR]: {
    singular: 'vendor',
    plural: 'vendors',
  },
}

const CACHE_KEY_PREFIX = 'scrambl:supplier-terminology'
export const DEFAULT_MODE = SupplierTerminologyMode.VENDOR
const modeCache = new Map<string, SupplierTerminologyMode>()
const listeners = new Set<
  (organizationId: string, mode: SupplierTerminologyMode) => void
>()
let currentStaticOrgId: string | null = null

export function isSupplierTerminologyMode(
  value: unknown,
): value is SupplierTerminologyMode {
  return (
    value === SupplierTerminologyMode.SUPPLIER ||
    value === SupplierTerminologyMode.VENDOR
  )
}

function getCacheKey(organizationId: string) {
  return `${CACHE_KEY_PREFIX}:${organizationId}`
}

export function readCachedMode(organizationId?: string | null) {
  if (!organizationId) return undefined

  const inMemory = modeCache.get(organizationId)
  if (inMemory) return inMemory

  if (typeof window === 'undefined') return undefined

  try {
    const stored = window.localStorage.getItem(getCacheKey(organizationId))
    if (isSupplierTerminologyMode(stored)) {
      modeCache.set(organizationId, stored)
      return stored
    }
  } catch {
    return undefined
  }

  return undefined
}

export function writeCachedMode(
  organizationId: string,
  mode: SupplierTerminologyMode,
) {
  modeCache.set(organizationId, mode)
  currentStaticOrgId = organizationId

  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(getCacheKey(organizationId), mode)
  } catch {
    // Local storage can be unavailable in private or embedded contexts.
  }
}

export function buildSupplierTerm(
  mode: SupplierTerminologyMode,
  organizationId?: string,
  isLoading = false,
): SupplierTerm {
  const terms = TERMS[mode]

  return {
    ...terms,
    Singular: formatEntityTerm(terms.singular, true, false),
    Plural: formatEntityTerm(terms.plural, true, false),
    mode,
    isVendor: mode === SupplierTerminologyMode.VENDOR,
    isLoading,
    organizationId,
  }
}

export function updateSupplierTerminology(
  organizationId: string,
  mode: SupplierTerminologyMode,
) {
  writeCachedMode(organizationId, mode)
  listeners.forEach(listener => listener(organizationId, mode))
}

export function setCurrentStaticSupplierTerminologyOrganization(
  organizationId: string | null,
) {
  currentStaticOrgId = organizationId
}

export function getSupplierTerminologyStatic(organizationId?: string) {
  const orgId = organizationId || currentStaticOrgId
  const mode = readCachedMode(orgId) ?? DEFAULT_MODE
  return buildSupplierTerm(mode, orgId ?? undefined)
}

export function subscribeSupplierTerminology(
  listener: (organizationId: string, mode: SupplierTerminologyMode) => void,
): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
